import { adminApi } from '@core/api/httpClient'
import type {
  OverridePropertyFeePayload,
  SavePropertyFeesPayload,
} from './propertyFeesContracts'
import {
  parseOfferingPrice,
  parsePropertyFeeScreen,
  parsePropertyFeeVersions,
} from './propertyFeesParser'
import type {
  OfferingPrice,
  PropertyFeeScreen,
  PropertyFeeVersion,
} from '../types/propertyFeeTypes'

const P = '/api/admin/properties'

function feesPath(propertyId: string): string {
  return `${P}/${propertyId}/fees`
}

/**
 * Every fee that applies to this property, on its market default unless the
 * property overrides it, together with the offering price they add up to.
 */
export async function getPropertyFees(propertyId: string): Promise<PropertyFeeScreen> {
  const { data } = await adminApi.get<unknown>(feesPath(propertyId))
  return parsePropertyFeeScreen(data)
}

/** The breakdown on its own, for callers that only need the price. */
export async function getPropertyOfferingPrice(propertyId: string): Promise<OfferingPrice> {
  const { data } = await adminApi.get<unknown>(`${feesPath(propertyId)}/offering-price`)
  return parseOfferingPrice(data)
}

/** Every value one fee has held on this property, newest first. */
export async function getPropertyFeeVersions(
  propertyId: string,
  feeDefinitionId: string,
): Promise<PropertyFeeVersion[]> {
  const { data } = await adminApi.get<unknown>(
    `${feesPath(propertyId)}/${feeDefinitionId}/versions`,
  )
  return parsePropertyFeeVersions(data)
}

/**
 * Overrides one fee. Append-only: the row in force is closed and a new one
 * opened, so the value a settled fee was charged under stays readable.
 */
export async function overridePropertyFee(
  propertyId: string,
  code: string,
  payload: OverridePropertyFeePayload,
): Promise<PropertyFeeScreen> {
  const { data } = await adminApi.patch<unknown>(`${feesPath(propertyId)}/${code}`, payload)
  return parsePropertyFeeScreen(data)
}

/** Overrides several fees in one transaction — the screen's Save. */
export async function savePropertyFees(
  propertyId: string,
  payload: SavePropertyFeesPayload,
): Promise<PropertyFeeScreen> {
  const { data } = await adminApi.patch<unknown>(feesPath(propertyId), payload)
  return parsePropertyFeeScreen(data)
}
